import { Send } from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { BidLineInput } from './LineItemsCard';

interface AdditionalInfoCardProps {
  readonly validUntil: string;
  readonly notes: string;
  readonly onValidUntilChange: (value: string) => void;
  readonly onNotesChange: (value: string) => void;
}

export function AdditionalInfoCard({
  validUntil,
  notes,
  onValidUntilChange,
  onNotesChange,
}: AdditionalInfoCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Additional Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm font-medium">Bid Valid Until</label>
          <input
            type="date"
            value={validUntil}
            onChange={(e) => onValidUntilChange(e.target.value)}
            className="w-full mt-1 rounded-md border px-3 py-2"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Notes (Optional)</label>
          <textarea
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Any additional information about your bid..."
            rows={3}
            className="w-full mt-1 rounded-md border px-3 py-2"
          />
        </div>
      </CardContent>
    </Card>
  );
}

interface EventDetailsCardProps {
  readonly event: {
    deliveryZone: string;
    deliverySiteName: string;
    requiredDeliveryDate: string;
    bidDeadline: string;
    estimatedValue: number;
    startingPrice?: number;
  };
  readonly formatCurrency: (amount: number) => string;
  readonly isReverseAuction: boolean;
}

export function EventDetailsCard({
  event,
  formatCurrency,
  isReverseAuction,
}: EventDetailsCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Event Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Delivery Zone</span>
          <span className="font-medium">{event.deliveryZone}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Delivery Site</span>
          <span className="font-medium">{event.deliverySiteName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Required By</span>
          <span className="font-medium">
            {new Date(event.requiredDeliveryDate).toLocaleDateString()}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Bid Deadline</span>
          <span className="font-medium">
            {new Date(event.bidDeadline).toLocaleDateString()}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Est. Value</span>
          <span className="font-medium">{formatCurrency(event.estimatedValue)}</span>
        </div>
        {isReverseAuction && event.startingPrice && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Starting Price</span>
            <span className="font-medium">{formatCurrency(event.startingPrice)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface BidSummaryCardProps {
  readonly bidLines: BidLineInput[];
  readonly bidTotal: number;
  readonly event: { currentLowestBid?: number };
  readonly isReverseAuction: boolean;
  readonly isValid: boolean;
  readonly isPending: boolean;
  readonly formatCurrency: (amount: number) => string;
  readonly onSubmit: () => void;
}

export function BidSummaryCard({
  bidLines,
  bidTotal,
  event,
  isReverseAuction,
  isValid,
  isPending,
  formatCurrency,
  onSubmit,
}: BidSummaryCardProps) {
  const currentLowest = event.currentLowestBid ?? 0;
  const showComparison = isReverseAuction && currentLowest > 0 && bidTotal > 0;
  const isBelowLowest = bidTotal < currentLowest;
  const difference = Math.abs(bidTotal - currentLowest);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bid Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Items</span>
          <span>{bidLines.length}</span>
        </div>
        <div className="flex justify-between text-lg font-semibold border-t pt-3">
          <span>Total</span>
          <span>{formatCurrency(bidTotal)}</span>
        </div>
        {showComparison && (
          <BidComparisonBadge
            isBelowLowest={isBelowLowest}
            difference={difference}
            formatCurrency={formatCurrency}
          />
        )}
        <Button
          className="w-full mt-4"
          disabled={!isValid || isPending}
          onClick={onSubmit}
        >
          <Send className="h-4 w-4 mr-2" />
          {isPending ? 'Submitting...' : 'Submit Bid'}
        </Button>
      </CardContent>
    </Card>
  );
}

interface BidComparisonBadgeProps {
  readonly isBelowLowest: boolean;
  readonly difference: number;
  readonly formatCurrency: (amount: number) => string;
}

function BidComparisonBadge({
  isBelowLowest,
  difference,
  formatCurrency,
}: BidComparisonBadgeProps) {
  const className = isBelowLowest
    ? 'bg-green-100 text-green-700'
    : 'bg-amber-100 text-amber-700';
  const symbol = isBelowLowest ? '✓' : '⚠';
  const label = isBelowLowest ? 'below' : 'above';

  return (
    <div className={`text-sm p-2 rounded ${className}`}>
      {symbol} {formatCurrency(difference)} {label} current lowest
    </div>
  );
}
